import { useTranslation, untranslated } from "@i18n";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { ImCross } from "react-icons/im";
import { IoIosWarning } from "react-icons/io";

import { useNavigate } from "react-router-dom";
import { BiLinkExternal } from "react-icons/bi";
import { getViewerUrl } from "@Helpers";
import { FileManagerAsPopup } from "@Features/files";
import { getFileDependencies, saveFileDependencies, removeFileDependency } from "@Actions/fileUpload";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";
import type { FileDependencyItem } from "@ServerTypes/fileOperations/fileOperations.schemas";

interface DependencyContentProps {
  workspaceId?: string;
  fileVersionId: string;
  accessPermission?: { write?: boolean };
  projectId: string;
  isEmptyContainer?: boolean;
}

function DependencyContent({
  workspaceId,
  fileVersionId,
  accessPermission,
  projectId,
  isEmptyContainer = false,
}: DependencyContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileDependencies = useSelector((state: RootState) => state.fileDependencies[fileVersionId]);
  const [isDialogVisible, setIsDialogVisible] = useState(false);

  useEffect(() => {
    if (fileVersionId) {
      dispatch(
        getFileDependencies({
          fileVersionId,
        }),
      );
    }
  }, [fileVersionId]);

  const handleDialogOpen = () => {
    if (!accessPermission?.write) {
      showToast();
      return;
    }
    setIsDialogVisible(true);
  };

  const handleFileSelectedCancelClick = () => {
    setIsDialogVisible(false);
  };

  const handleFileSelectedButtonClick = async (
    selectedFiles: Record<string, { transmittalVersionId?: string; fileVersion: string }>,
  ) => {
    const res = await saveFileDependencies({
      fileVersionId,
      internalFileDependencies: Object.values(selectedFiles)
        .filter((val) => !val.transmittalVersionId && val.fileVersion)
        .map((file) => file.fileVersion),
      externalFileDependencies: Object.values(selectedFiles)
        .filter((val) => val.transmittalVersionId && val.fileVersion)
        .map((file) => file.fileVersion),
    });

    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Dependency saved successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      dispatch(
        getFileDependencies({
          fileVersionId,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          // `saveFileDependenciesResponse` declares `errorMessage` optional, and a toast with no
          // content is dropped by the reducer — fall back rather than assert it present.
          content: untranslated(res.data.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }

    handleFileSelectedCancelClick();
  };

  const handleRemoveFileDependency = async (dependency: FileDependencyItem) => {
    let internalFileDependencies: string[] = [];
    let externalFileDependencies: string[] = [];

    if (dependency.type == "INTERNAL_FILE_DEPENDENCY") {
      internalFileDependencies = [dependency.fileVersionId!];
    } else {
      externalFileDependencies = [dependency.fileVersionId!];
    }
    const res = await removeFileDependency({
      fileVersionId,
      internalFileDependencies,
      externalFileDependencies,
    });
    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Dependency removed successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      dispatch(
        getFileDependencies({
          fileVersionId,
        }),
      );
    }
  };

  const handleOpenLink = (dependency: FileDependencyItem, newTab: boolean) => {
    const location = getViewerUrl({
      fileVersionId: dependency.latestFileVersionId ?? "",
      transmittalVersionId:
        dependency.type == "EXTERNAL_FILE_DEPENDENCY" ? (dependency.latestTransmittalVersionId ?? "") : "",
      spaceId: workspaceId,
      projectId: dependency.projectId ?? "",
    });
    if (location) {
      newTab ? window.open(location, "_blank") : navigate(location);
    }
  };

  const showToast = () => {
    dispatch(
      showToastNotification({
        content: t("Insufficient permission to perform this action."),
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  return (
    <>
      {isDialogVisible && (
        <FileManagerAsPopup
          projectId={projectId}
          handleFileSelectedButtonClick={handleFileSelectedButtonClick}
          handleFileSelectedCancelClick={handleFileSelectedCancelClick}
          workspaceId={workspaceId}
          id="fileManagerAsPopup"
          hideIssues
          hideTasks
          hideSchedules
          hideEstimates
          hideContracts
          heading={t("Add Dependencies")}
          disableFiles={[fileVersionId]}
          hideFilesWithoutVersion
        />
      )}

      <div className="flex flex-col gap-4">
        <div data-testid="addNewDependenciesBtn">
          <ButtonComponent
            disabled={isEmptyContainer}
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={handleDialogOpen}
          >
            <Text>{t("Add New")}</Text>
          </ButtonComponent>
        </div>

        {isEmptyContainer && (
          <div className="flex gap-1 flex-col text-disabled_text_gray items-center">
            <IconComponent name="notAllowed" />
            <Text>{t("Adding dependencies to empty containers is not allowed")}</Text>
          </div>
        )}
        <div style={{ paddingTop: "1rem" }}>
          {fileDependencies?.map((dependency: FileDependencyItem) => (
            <div
              key={`dependency_${dependency.fileVersionId}`}
              style={{
                display: "grid",
                gridTemplateColumns: "auto auto",
                justifyContent: "space-between",
                paddingBottom: "1rem",
              }}
            >
              <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                <ButtonComponent
                  buttonType={BUTTON_TYPES.TEXT_BUTTON}
                  type="button"
                  onClick={() => handleOpenLink(dependency, false)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key.toLowerCase() === "e") e.preventDefault();
                  }}
                >
                  {dependency.name}
                </ButtonComponent>
                <BiLinkExternal onClick={() => handleOpenLink(dependency, true)} />

                {dependency.status !== "DELETED" && dependency.fileVersionId !== dependency.latestFileVersionId && (
                  <IconComponent
                    name="infoYellow"
                    width="1.25rem"
                    height="1.25rem"
                    toolTipContent={t("This file's version has been updated.")}
                    position="bottom"
                  />
                )}
                {dependency.status == "DELETED" && (
                  <span className="ps-4">
                    <ToolTipComponent
                      showTipPointer={false}
                      position="bottom"
                      content={t("File {{name}} version was deleted", { name: dependency.name })}
                    >
                      <IoIosWarning color="red" size={16} />
                    </ToolTipComponent>
                  </span>
                )}
              </div>

              <ButtonComponent
                buttonType={BUTTON_TYPES.TEXT_BUTTON}
                type="button"
                onClick={() => handleRemoveFileDependency(dependency)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key.toLowerCase() === "e") e.preventDefault();
                }}
                aria-label="Remove file dependency"
              >
                <ImCross />
              </ButtonComponent>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default DependencyContent;
